import React, { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import MuiDialogTitle from '@mui/material/DialogTitle';
import MuiDialogContent from '@mui/material/DialogContent';
import MuiDialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import Button from '@mui/material/Button';
import { FaPhone } from 'react-icons/fa';
import { getAPICall } from '../../dataFetcher/axiosMethodCalls';
import { ShippingAddressDetails, IShippingAddressDetails } from './ShippingInformation';

export interface ICountry {
  country: string;
  countryCode: string;
}

export type AddressListPopupProps = {
  props: {
    open: boolean;
    closeAddressListPopup: any;
    selectAddressId: any;
    countryList: Array<ICountry>;
    openAddNewAddressPopup: any;
  };
};

const StyledDialogTitleRoot = styled(MuiDialogTitle)(({ theme }) => ({
  margin: 0,
  padding: theme.spacing(2),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

const StyledDialogTitle = styled('div')(({ theme }) => ({
  margin: 0,
  padding: theme.spacing(2),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  position: 'relative',
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  right: theme.spacing(1),
  top: theme.spacing(1),
  color: theme.palette.grey[500],
}));

export const DialogTitle = ({ children, onClose, id }: DialogTitleProps) => (
  <StyledDialogTitle id={id}>
    <Typography variant="h6">{children}</Typography>
    {onClose ? (
      <CloseButton aria-label="close" onClick={onClose}>
        <CloseIcon />
      </CloseButton>
    ) : null}
  </StyledDialogTitle>
);

export const DialogContent = styled(MuiDialogContent)(({ theme }) => ({
  padding: theme.spacing(2),
}));

export const DialogActions = styled(MuiDialogActions)(({ theme }) => ({
  margin: 0,
  padding: theme.spacing(1),
}));

export type DialogTitleProps = {
  children: React.ReactNode;
  onClose?: () => void;
  id?: string;
};

const AddressListPopup = ({ props }: AddressListPopupProps): JSX.Element => {
  const [shippingPrimaryAddress, setShippingPrimaryAddress] = useState<
    ShippingAddressDetails | undefined
  >();
  const [shippingOtherAddresses, setShippingOtherAddresses] = useState(
    Array<ShippingAddressDetails>()
  );
  const [selectedAddress, setSelectedAddress] = useState<ShippingAddressDetails>();
  const [selectOrderAddresses, setSelectOrderAddresses] = useState<boolean>();

  useEffect(() => {
    if (props.open) {
      getShippingAddressesApiCall();
    }
  }, [props.open]);

  function getShippingAddressesApiCall() {
    getAPICall(`${process.env.REACT_APP_PROFILE_URL}/profile/getaddresses`).then(function (
      response: any
    ) {
      if (response && response.data && response.data.length > 0) {
        const primaryAddressList = response.data.filter((x: ShippingAddressDetails) => x.IsPrimary);
        var primaryAddress: ShippingAddressDetails;
        if (primaryAddressList.length > 0) {
          primaryAddress = primaryAddressList[0];
        } else {
          primaryAddress = response.data[0];
        }
        setShippingPrimaryAddress(primaryAddress);
        setShippingOtherAddresses([
          ...response.data.filter(
            (x: ShippingAddressDetails) => x.AddressId !== primaryAddress?.AddressId
          ),
        ]);
      }
    });
  }

  function getAddressFieldValue(value: string) {
    if (value === null || value === '') {
      return <></>;
    } else {
      return <>{value},</>;
    }
  }
  function getCountryName(countryCode: string) {
    let country = props.countryList.filter((x) => x.countryCode === countryCode);
    if (country.length > 0) {
      return country[0].country;
    }
    return countryCode;
  }
  const handleClose = () => {
    props.closeAddressListPopup();
  };
  const updateAddress = () => {
    props.closeAddressListPopup(selectedAddress, true, selectOrderAddresses);
  };
  const addNewAddress = () => {
    props.closeAddressListPopup();
    props.openAddNewAddressPopup();
  };

  function onAddressChange(address: ShippingAddressDetails | undefined) {
    if (address !== undefined) {
      setSelectedAddress(address);
    }
  }

  return (
    <div className="addresslistpopupdialog">
      <Dialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={props.open}
        BackdropProps={{ invisible: true }}
        PaperProps={{
          style: {
            minHeight: '30vh',
            minWidth: '70vw',
            borderRadius: '5px',
          },
        }}
      >
        <DialogTitle id="customized-dialog-title" onClose={handleClose}>
          {''}
        </DialogTitle>
        <DialogContent className="addresslist">
          <div className="addresslist">
            <div className="primaryaddress">
              <p className="addressheader">Primary</p>

              {shippingPrimaryAddress && (
                <div>
                  <input
                    type="radio"
                    id="primary"
                    name="addressgroup"
                    className="radiobutton"
                    defaultChecked={props.selectAddressId === shippingPrimaryAddress.AddressId}
                    onChange={() => onAddressChange(shippingPrimaryAddress)}
                  ></input>
                  <span className="spanpaymenttype">
                    <>
                      {shippingPrimaryAddress.Street1},<> </>
                      {getAddressFieldValue(shippingPrimaryAddress.Street2)}
                      <> </>
                      {shippingPrimaryAddress.City},<> </> {shippingPrimaryAddress.State},<> </>{' '}
                      {shippingPrimaryAddress.Zip !== null && <>{shippingPrimaryAddress.Zip},</>}
                      <> </>
                      {props.countryList && getCountryName(shippingPrimaryAddress.Country)}
                      <> </>
                      {shippingPrimaryAddress.Phone !== null && (
                        <>
                          ( <FaPhone className="faphoneicon"></FaPhone>
                          {shippingPrimaryAddress.Phone})
                        </>
                      )}
                    </>
                  </span>
                </div>
              )}
            </div>
            <div className="otheraddress">
              {shippingOtherAddresses && shippingOtherAddresses.length > 0 && (
                <>
                  <p className="addressheader">Other</p>
                  {shippingOtherAddresses.map((address) => (
                    <div className="otheraddresslist" key={address.AddressId}>
                      <input
                        type="radio"
                        id={address.AddressId}
                        name="addressgroup"
                        className="radiobutton"
                        defaultChecked={props.selectAddressId === address.AddressId}
                        onChange={() => onAddressChange(address)}
                      ></input>
                      <span key={address.AddressId} className="addressdetailtext">
                        {address.Street1},<> </>
                        {getAddressFieldValue(address.Street2)}
                        <> </>
                        {address.City},<> </> {address.State},<> </>{' '}
                        {address.Zip !== null && <>{address.Zip},</>}
                        <> </>
                        {props.countryList && getCountryName(address.Country)}
                        <> </>
                        {address.Phone !== null && (
                          <>
                            ( <FaPhone className="faphoneicon"></FaPhone>
                            {address.Phone})
                          </>
                        )}
                      </span>
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        </DialogContent>
        <div className="addresslistpopupbuttons">
          <DialogActions>
            <Button autoFocus onClick={updateAddress} color="primary">
              USE THIS ADDRESS
            </Button>
            <Button
              onClick={addNewAddress}
              color="primary"
              disabled={shippingOtherAddresses.length >= 4}
            >
              ADD NEW ADDRESS
            </Button>
          </DialogActions>
        </div>
      </Dialog>
    </div>
  );
};

export default AddressListPopup;
